<template>
  <DialogElement
    v-show-fade-content
    :closed-by="'any'"
    :aria-label="t('settings.componentTitle')"
    class="flex absolute settings-container w-full h-auto right-0 p-0 m-0 left-auto"
    @close="close()"
  >
    <div
      class="settings-dialog w-full pb-10 overflow-y-auto overscroll-contain show-content-fade"
    >
      <h2 class="fixed w-full text-center pb-3 pt-3 text-xl">
        {{ t("settings.title") }}
      </h2>
      <form class="pt-12 px-6">
        <div
          v-if="attributesStore.isMobile && wpsClient.media.isPDF"
          class="mt-6"
        >
          <span class="settings-label">
            {{ t("zoomSelector.name") }}
          </span>
          <div class="pt-3">
            <ZoomSelector :vertical-mode="scrollMode" />
          </div>

          <ZoomCard :vertical-mode="scrollMode" :floating-mode="false" />
        </div>

        <div class="mt-6">
          <span class="settings-label">
            {{ t("settings.theme") }}
          </span>
          <ThemeSelector
            v-model="settingsStore.global.theme"
            :setting-theme="settingTheme"
          />
        </div>

        <div
          v-if="!wpsClient.media.isPDF && !wpsClient.media.isFXL"
          class="mt-6"
        >
          <span class="settings-label">
            {{ t("settings.advancedSettings") }}
          </span>
          <CustomToggle
            v-model="fontOverrideModel"
            name="fontOverride"
            :label="t('settings.advancedSettings')"
          />
        </div>

        <div v-if="wpsClient.media.isMediaOverlay" class="mt-6">
          <span class="settings-label">
            {{ t("settings.autoTurn") }}
          </span>
          <CustomToggle
            v-model="settingsStore.perContent.ttsAutoTurn"
            name="autoTurn"
            :label="t('settings.autoTurn')"
          />
        </div>

        <div
          v-if="
            !wpsClient.media.isPDF &&
            !wpsClient.media.isFXL &&
            !wpsClient.interactive
          "
          class="mt-6"
        >
          <span class="settings-label">
            {{ t("settings.fontSize") }}
          </span>
          <FontSizeSelector v-model="settingsStore.perContent.fontSize" />
        </div>

        <div
          v-if="
            !wpsClient.media.isPDF &&
            !wpsClient.media.isFXL &&
            !wpsClient.interactive
          "
          class="mt-6"
        >
          <span class="settings-label">
            {{ t("settings.fontFamily") }}
          </span>
          <CustomSelectList
            v-model="settingsStore.perContent.fontFamily"
            :list-name="settingFontFamily.name"
            :items-list="settingFontFamily.options"
            :list-label="t('settings.fontFamilySelector')"
          />
        </div>

        <div
          v-if="!wpsClient.media.isFXL && !wpsClient.interactive"
          class="mt-6"
        >
          <span class="settings-label">
            {{ t("settings.overflow") }}
          </span>
          <CustomToggle
            v-model="settingsStore.perContent.scroll"
            name="toggleScrollView"
            :label="t('settings.toggleScrollView')"
          />
        </div>

        <div
          v-if="!wpsClient.media.isPDF && !wpsClient.interactive"
          class="mt-6"
        >
          <span class="settings-label">
            {{ t("settings.layout") }}
          </span>
          <SelectorButtonGroup
            v-model="settingsStore.perContent.columnCount"
            :buttons-list="
              wpsClient.media.isPDF ? settingLayout : settingLayoutFXL
            "
            :buttons-disabled="scrollMode"
            :ui-container="'.settings-dialog'"
          />
        </div>

        <div
          v-if="wpsClient.media.isPDF && !wpsClient.interactive"
          class="mt-6"
        >
          <span class="settings-label">
            {{ t("settings.firstPageByItself") }}
          </span>
          <CustomToggle
            v-model="settingsStore.perContent.firstPageByItself"
            name="firstPageByItself"
            :label="t('settings.firstPageByItself')"
            :buttons-disabled="attributesStore.isMobile"
          />
        </div>

        <div
          v-if="wpsClient.media.isPDF && !wpsClient.interactive"
          class="mt-6"
        >
          <span class="settings-label">
            {{ t("settings.layout") }}
          </span>
          <SelectorButtonGroup
            v-model="settingsStore.perContent.pageLayoutPDF"
            :buttons-list="pageLayoutPDF"
            :buttons-disabled="attributesStore.isMobile"
            :ui-container="'.settings-dialog'"
          />
        </div>
      </form>
    </div>
    <CloseDialogButton class="absolute close-sidebar" />
  </DialogElement>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { UserSetting } from "@/helpers/settings";
import { settingName, defaultSettings as settings } from "@/helpers/constants";
import ThemeSelector from "@/components/settings/ThemeSelector.vue";
import FontSizeSelector from "@/components/settings/FontSizeSelector.vue";
import CustomSelectList from "@/components/settings/CustomSelectList.vue";
import SelectorButtonGroup from "@/components/settings/SelectorButtonGroup.vue";
import CustomToggle from "@/components/settings/CustomToggle.vue";
import ZoomSelector from "@/components/shared/ZoomSelector.vue";
import ZoomCard from "@/components/shared/ZoomCard.vue";

import { useAttributesStore } from "@/stores/attributes";
import { useSettingsStore } from "@/stores/settings";

import { useWpsClient } from "@/wps/client";

import { useUILayers } from "../UILayers";
import DialogElement from "../shared/DialogElement.vue";
import CloseDialogButton from "../shared/CloseDialogButton.vue";

const { t } = useI18n();

const wpsClient = useWpsClient();

const { close } = useUILayers();

const attributesStore = useAttributesStore();
const settingsStore = useSettingsStore();

const scrollMode = computed<boolean>(() => settingsStore.perContent.scroll);
// Font override must be inverted, because if it is enabled (true for keep publication styles),
// the font override should not be applied (false as property value to send)
const fontOverrideModel = computed({
  get: () => !settingsStore.perContent.fontOverride,
  set: (value) => {
    settingsStore.perContent.fontOverride = value ? false : true;
  },
});

const settingTheme = settings.find(
  (setting: UserSetting) => setting.name === settingName.theme
);

const settingFontFamily = settings.find(
  (setting: UserSetting) => setting.name === settingName.fontFamily
);

const settingLayout = settings.find(
  (setting: UserSetting) => setting.name === settingName.pageLayout
);

// shallow copy to not modify the original object
const settingLayoutFXL = { ...settingLayout };
settingLayoutFXL.options = settingLayout?.options?.filter(
  (option: { label: string }) => option.label !== "Dual"
);

const pageLayoutPDF = settings.find(
  (setting: UserSetting) => setting.name === settingName.pageLayoutPDF
);

const vShowFadeContent = {
  mounted: (el) => {
    const nav = el.getElementsByClassName("show-content-fade")[0];
    setTimeout(() => nav?.classList.add("show-nav"), 300);
  },
  onBeforeMount: (el) => {
    const nav = el.getElementsByClassName("show-content-fade")[0];
    nav?.classList.remove("show-nav");
  },
};
</script>

<style lang="scss" scoped>
@use "@/assets/styles/reader.scss" as *;
.settings-container {
  bottom: 0;
  z-index: 3;
  height: 49vh;
  max-height: var(--safe-height);
  color: var(--DM__theme-font-color);
  background-color: var(--DM__theme-fill-color);
  border-bottom: $spacingM var(--DM__theme-fill-color) solid;
  border-top: $spacingM var(--DM__theme-fill-color) solid;
  border-top-right-radius: $spacingM;
  border-top-left-radius: $spacingM;
  box-shadow: 0 4px 23px rgb(0 0 0 / 9%);
  border: 3px solid rgba(158, 158, 158, 0.2);

  @media (min-width: $screenS) {
    height: auto;
    max-height: calc(var(--safe-height) - 3 * $barHeight);
    border-radius: 0;
    right: $spacingM;
    top: $barHeight + $spacingM;
    max-width: $screenXS;
    bottom: auto;
  }

  .settings-dialog {
    h2 {
      border-bottom: 1px solid var(--DM__theme-hover-color);
      background-color: var(--DM__theme-fill-color);
      z-index: 1;
      max-width: calc(100% - 6px);
      border-top-right-radius: $spacingM;
      border-top-left-radius: $spacingM;
      @media (min-width: $screenS) {
        max-width: calc($screenXS - 6px);
        border-radius: 0;
      }
    }

    @include scrollBar();
  }

  .close-sidebar {
    z-index: 2;
    top: 0.65rem;
  }

  .settings-label {
    @apply mx-0 my-auto w-28 pr-3 font-bold;
  }
}
</style>
